import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Table, Badge, Spinner, Button, Row, Col, Nav } from 'react-bootstrap';
import { FaArrowLeft, FaTrophy, FaStar, FaMedal } from 'react-icons/fa';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../services/api';
import './TournamentProPoints.css';

const TournamentProPoints = () => {
  const { torneoId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clasificacion, setClasificacion] = useState([]);
  const [torneoNombre, setTorneoNombre] = useState('');
  const [error, setError] = useState(null);
  const [procesando, setProcesando] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [sedes, setSedes] = useState([]);
  const [sedeSeleccionada, setSedeSeleccionada] = useState(null);

  useEffect(() => {
    const fetchSedes = async () => {
      try {
        const res = await api.get(`/postgres/torneos/${torneoId}/sedes`);
        if (res.data.success) setSedes(res.data.data);
      } catch (e) {
        console.error('Error al cargar sedes:', e);
      }
    };
    fetchSedes();
  }, [torneoId]);

  useEffect(() => {
    fetchClasificacion();
  }, [torneoId, sedeSeleccionada]);

  const fetchClasificacion = async () => {
    try {
      setLoading(true);
      setError(null);

      // Obtener datos del torneo
      const torneoResponse = await api.get(`/postgres/torneos/${torneoId}`);
      setTorneoNombre(torneoResponse.data.data.nombre);

      // Obtener clasificación de ProPoints (con filtro de sede si aplica)
      const params = sedeSeleccionada ? `?sedeId=${sedeSeleccionada}` : '';
      const response = await api.get(`/reports/propoints-torneo/${torneoId}${params}`);

      if (response.data.success) {
        setClasificacion(response.data.data);
      } else {
        setError('Error al cargar la clasificación');
      }
    } catch (error) {
      console.error('Error al cargar clasificación:', error);
      setError('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleActualizarNiveles = async () => {
    try {
      setProcesando(true);
      setMensaje(null);

      const response = await api.post(`/reports/actualizar-niveles-torneo/${torneoId}`);

      if (response.data.success) {
        setMensaje({
          tipo: 'success',
          texto: `✅ ${response.data.data.actualizados} niveles actualizados correctamente`
        });

        // Recargar clasificación
        await fetchClasificacion();
      }
    } catch (error) {
      console.error('Error al actualizar niveles:', error);
      setMensaje({
        tipo: 'danger',
        texto: '❌ Error al actualizar niveles'
      });
    } finally {
      setProcesando(false);
    }
  };

  const handleVerificarLogros = async () => {
    try {
      setProcesando(true);
      setMensaje(null);

      const response = await api.post(`/reports/verificar-logros-especiales-torneo/${torneoId}`);

      if (response.data.success) {
        setMensaje({
          tipo: 'success',
          texto: '✅ Verificación de logros especiales completada'
        });

        // Recargar clasificación
        await fetchClasificacion();
      }
    } catch (error) {
      console.error('Error al verificar logros:', error);
      setMensaje({
        tipo: 'danger',
        texto: '❌ Error al verificar logros especiales'
      });
    } finally {
      setProcesando(false);
    }
  };

  const getPosicionMedalla = (posicion) => {
    if (posicion === 1) return { icon: <FaTrophy />, color: '#FFD700', text: '🥇' };
    if (posicion === 2) return { icon: <FaMedal />, color: '#C0C0C0', text: '🥈' };
    if (posicion === 3) return { icon: <FaMedal />, color: '#CD7F32', text: '🥉' };
    return { icon: null, color: '#666', text: `${posicion}º` };
  };

  const getLogrosIndividualesTexto = (logros) => {
    const items = [];
    if (logros.participacion > 0) items.push(`✅${logros.participacion}`);
    if (logros.victoria > 0) items.push(`🏆${logros.victoria}`);
    if (logros.destacado > 0) items.push(`⭐${logros.destacado}`);
    if (logros.el_jugon > 0) items.push(`🎮${logros.el_jugon}`);
    if (logros.el_cerrojo > 0) items.push(`🛡️${logros.el_cerrojo}`);
    if (logros.el_lider > 0) items.push(`👑${logros.el_lider}`);
    if (logros.el_mejor_companero > 0) items.push(`🤝${logros.el_mejor_companero}`);
    if (logros.el_desequilibrante > 0) items.push(`⚡${logros.el_desequilibrante}`);
    if (logros.jugador_de_equipo > 0) items.push(`👥${logros.jugador_de_equipo}`);
    return items.length > 0 ? items.join(' · ') : '-';
  };

  const getLogrosEspecialesTexto = (logrosArray) => {
    if (!logrosArray || logrosArray.length === 0) return '-';
    return logrosArray.map(logro => {
      const iconos = {
        'Constancia': '🎖️',
        'Ganador Incansable': '🏆',
        'Mayor Progresión': '📈'
      };
      const icono = iconos[logro.nombre] || '🏅';
      return `${icono} ${logro.nombre} (+${logro.puntos})`;
    }).join(' | ');
  };

  if (loading) {
    return (
      <AdminLayout title="Clasificación ProPoints">
        <Container className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Cargando clasificación...</p>
        </Container>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="Clasificación ProPoints">
        <Container>
          <div className="alert alert-danger">{error}</div>
          <Button variant="outline-secondary" onClick={() => navigate('/admin/matches')}>
            <FaArrowLeft className="me-2" />
            Volver
          </Button>
        </Container>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={`Clasificación ProPoints - ${torneoNombre}`}>
      <Container fluid className="tournament-propoints-container">
        <div className="mb-4 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
          <Button variant="outline-secondary" onClick={() => navigate('/admin/matches')} size="sm">
            <FaArrowLeft className="me-2" />
            <span className="d-none d-sm-inline">Volver a Torneos</span>
            <span className="d-inline d-sm-none">Volver</span>
          </Button>
          <h2 className="mb-0 fs-4 fs-md-2">
            <FaTrophy className="me-2 text-warning" />
            <span className="d-none d-md-inline">{torneoNombre}</span>
            <span className="d-inline d-md-none">{torneoNombre.length > 20 ? torneoNombre.substring(0, 20) + '...' : torneoNombre}</span>
          </h2>
        </div>

        {/* Selector de sede */}
        {sedes.length > 1 && (
          <Nav variant="tabs" className="mb-3" activeKey={sedeSeleccionada ?? 'todas'} onSelect={(k) => setSedeSeleccionada(k === 'todas' ? null : parseInt(k))}>
            <Nav.Item>
              <Nav.Link eventKey="todas">Todas las sedes</Nav.Link>
            </Nav.Item>
            {sedes.map(sede => (
              <Nav.Item key={sede.sede_id}>
                <Nav.Link eventKey={sede.sede_id}>{sede.sede_nombre}</Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        )}

        {clasificacion.length === 0 ? (
          <Card>
            <Card.Body className="text-center py-5">
              <p className="text-muted">No hay futbolistas con ProPoints en este torneo{sedeSeleccionada ? ' para esta sede' : ''}</p>
            </Card.Body>
          </Card>
        ) : (
          <>
            {/* Leyenda de Logros */}
            <Card className="mb-3">
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <h6 className="fw-bold mb-2">📊 Logros Individuales</h6>
                    <div className="leyenda-logros">
                      <span className="me-3">✅ Participación</span>
                      <span className="me-3">🏆 Victoria</span>
                      <span className="me-3">⭐ Destacado</span>
                      <span className="me-3">🎮 El Jugón</span>
                      <span className="me-3">🛡️ El Cerrojo</span>
                      <span className="me-3">👑 El Líder</span>
                      <span className="me-3">🤝 El Mejor Compañero</span>
                      <span className="me-3">⚡ El Desequilibrante</span>
                      <span className="me-3">👥 Jugador de Equipo</span>
                    </div>
                  </Col>
                  <Col md={6}>
                    <h6 className="fw-bold mb-2">🏆 Logros Especiales</h6>
                    <div className="leyenda-logros">
                      <span className="me-3">🎖️ Constancia</span>
                      <span className="me-3">🏆 Ganador Incansable</span>
                      <span className="me-3">📈 Mayor Progresión</span>
                    </div>
                  </Col>
                </Row>

                {/* Botones de Mantenimiento */}
                <Row className="mt-3 pt-3 border-top">
                  <Col>
                    <div className="d-flex gap-2 flex-wrap">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleActualizarNiveles}
                        disabled={procesando}
                      >
                        {procesando ? '⏳ Procesando...' : '🔄 Actualizar Niveles'}
                      </Button>
                      <Button
                        variant="success"
                        size="sm"
                        onClick={handleVerificarLogros}
                        disabled={procesando}
                      >
                        {procesando ? '⏳ Procesando...' : '🔍 Verificar Logros Especiales'}
                      </Button>
                    </div>

                    {/* Mensaje de resultado */}
                    {mensaje && (
                      <div className={`alert alert-${mensaje.tipo} mt-2 mb-0 py-2 px-3`} role="alert">
                        <small>{mensaje.texto}</small>
                      </div>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card>
              <Card.Header className="bg-primary text-white">
                <h5 className="mb-0">
                  <FaStar className="me-2" />
                  {sedeSeleccionada
                    ? `Clasificación — ${sedes.find(s => s.sede_id === sedeSeleccionada)?.sede_nombre ?? 'Sede'} (${clasificacion.length} futbolistas)`
                    : `Clasificación General (${clasificacion.length} futbolistas)`
                  }
                </h5>
              </Card.Header>
              <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0 propoints-table">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '80px' }}>Pos.</th>
                      <th>Futbolista</th>
                      <th style={{ width: '100px' }}>Nivel</th>
                      <th style={{ width: '120px' }} className="text-center">ProPoints</th>
                      <th style={{ width: '100px' }} className="text-center">Partidos</th>
                      <th>Logros Individuales</th>
                      <th>Logros Especiales</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clasificacion.map((futbolista) => {
                      const medalla = getPosicionMedalla(futbolista.posicion);
                      return (
                        <tr key={futbolista.futbolista_id} className={futbolista.posicion <= 3 ? 'podium-row' : ''}>
                          <td className="text-center fw-bold" style={{ fontSize: '1.2rem' }}>
                            <span style={{ color: medalla.color }}>{medalla.text}</span>
                          </td>
                          <td>
                            <div className="fw-bold">{futbolista.futbolista_nombre}</div>
                            <small className="text-muted">
                              {futbolista.ano_nacimiento ? `Año ${futbolista.ano_nacimiento}` : ''}
                            </small>
                          </td>
                          <td>
                            <div className="nivel-text" style={{ color: futbolista.nivel.color }}>
                              {futbolista.nivel.icono} {futbolista.nivel.nombre}
                            </div>
                          </td>
                          <td className="text-center">
                            <div className="propoints-total fw-bold text-primary" style={{ fontSize: '1.3rem' }}>
                              {futbolista.propoints_total} PP
                            </div>
                            <small className="text-muted">
                              {futbolista.propoints_individuales} ind. + {futbolista.propoints_especiales} esp.
                            </small>
                          </td>
                          <td className="text-center">
                            <Badge bg="info">{futbolista.partidos_jugados}</Badge>
                          </td>
                          <td>
                            <div className="logros-text">
                              {getLogrosIndividualesTexto(futbolista.logros_individuales)}
                            </div>
                          </td>
                          <td>
                            <div className="logros-text">
                              {getLogrosEspecialesTexto(futbolista.logros_especiales)}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>

          {/* Estadísticas generales */}
          <Card className="mt-4">
            <Card.Header className="bg-light">
              <h6 className="mb-0">📊 Estadísticas del Torneo</h6>
            </Card.Header>
            <Card.Body>
              <div className="row text-center g-3">
                <div className="col-6 col-md-3">
                  <h4 className="text-primary mb-1">{clasificacion.length}</h4>
                  <small className="text-muted d-block">Futbolistas</small>
                </div>
                <div className="col-6 col-md-3">
                  <h4 className="text-success mb-1">
                    {clasificacion.reduce((sum, f) => sum + f.propoints_total, 0)}
                  </h4>
                  <small className="text-muted d-block">ProPoints Totales</small>
                </div>
                <div className="col-6 col-md-3">
                  <h4 className="text-info mb-1">
                    {clasificacion.reduce((sum, f) => sum + f.partidos_jugados, 0)}
                  </h4>
                  <small className="text-muted d-block">Partidos Jugados</small>
                </div>
                <div className="col-6 col-md-3">
                  <h4 className="text-warning mb-1">
                    {clasificacion.filter(f => f.logros_especiales.length > 0).length}
                  </h4>
                  <small className="text-muted d-block">Con Logros Especiales</small>
                </div>
              </div>
            </Card.Body>
          </Card>
          </>
        )}
      </Container>
    </AdminLayout>
  );
};

export default TournamentProPoints;

